/**
 * ComplianceRegisterWidget — where each legal requirement stands today.
 *
 * One row per ACTIVE obligation, worst first, so the table is read top-down as
 * a work list rather than scanned. Status is computed server-side by the
 * register's own derive_status, so this cannot disagree with the Compliance
 * Register page a reader opens to act on a row.
 *
 * Three things this deliberately does NOT do:
 *
 * - It does not follow the date filter. Each row is a STATE ("where does this
 *   requirement stand now"), not an event in a period. Filtering by period
 *   would hide an overdue requirement whose due date fell before the window —
 *   exactly the row that needs attention. The header says so.
 * - It does not show 0% when a site has adopted nothing. Applicability is per
 *   site; an unanswered register is unanswered, not failing, so the rate reads
 *   "Not assessed".
 * - It does not count "Due soon" as a miss. Only requirements that have come
 *   due are in the compliance-rate denominator.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchComplianceRegister } from '../api/client';
import { CARD, HEADING } from '../ui';

/** Status → pill styling. Keys are the server's status codes. */
const PILL = {
  not_met: 'bg-red-100 text-red-800 ring-red-200',
  overdue: 'bg-red-50 text-red-700 ring-red-200',
  evidence_missing: 'bg-amber-100 text-amber-800 ring-amber-200',
  due_soon: 'bg-amber-50 text-amber-700 ring-amber-200',
  pending: 'bg-gray-100 text-gray-600 ring-gray-200',
  met_late: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  met: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  no_occurrence: 'bg-gray-50 text-gray-500 ring-gray-200',
};

/** Statuses that need action, in the order they should be worked. */
const ACTION_STATES = ['not_met', 'overdue', 'evidence_missing', 'due_soon'];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ComplianceRegisterWidget({
  title = 'Legal Compliance Update',
  limit = 25,
}) {
  const { siteId, departmentId, dimensions, refreshMs } = useDashboard();
  const [showAll, setShowAll] = useState(false);
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';
  const effectiveLimit = showAll ? 200 : limit;

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { limit: effectiveLimit };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      return fetchComplianceRegister(body, { signal });
    },
    [siteId, departmentId, dimsKey, effectiveLimit],
    { refreshMs },
  );

  const rows = data?.data || [];
  const counts = data?.counts || {};
  const rate = data?.compliance_rate;
  const applicable = data?.applicable ?? 0;

  const actionable = ACTION_STATES.reduce(
    (n, s) => n + rows.filter((r) => r.status === s).length, 0,
  );

  return (
    <div className={`${CARD} p-4`}>
      <div className="mb-1 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className={HEADING}>{title}</h3>
          <p className="mt-0.5 text-[11px] text-gray-500">
            Current state of each applicable requirement — not filtered by the
            selected period.
          </p>
        </div>
        <div className="shrink-0 text-right">
          {rate === null || rate === undefined ? (
            <span className="text-xs text-gray-400">Not assessed</span>
          ) : (
            <>
              <span className="text-2xl font-semibold tabular-nums text-gray-900">
                {rate}%
              </span>
              <span className="ml-1 text-xs text-gray-500">compliant</span>
              <p className="text-[11px] text-gray-400">
                {data.met} of {data.judged} requirement
                {data.judged === 1 ? '' : 's'} that have come due
              </p>
            </>
          )}
        </div>
      </div>

      {/* Status summary — the shape of the register before the detail. */}
      {!loading && !error && applicable > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 border-b border-gray-100 pb-3">
          {Object.entries(counts).map(([label, n]) => (
            <span
              key={label}
              className="rounded bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200"
            >
              {label} <span className="font-semibold tabular-nums">{n}</span>
            </span>
          ))}
        </div>
      )}

      {loading && <div className="py-10 text-center text-sm text-gray-400">Loading…</div>}
      {error && (
        <div className="py-10 text-center text-sm text-red-600">
          Could not load the compliance register.
        </div>
      )}

      {!loading && !error && applicable === 0 && (
        <div className="py-8 text-center text-sm text-gray-400">
          No requirements have been declared applicable to this site yet, so
          compliance cannot be assessed. Adopt obligations in the Compliance
          Register to populate this table.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-3 font-semibold">Requirement</th>
                  <th className="py-2 pr-3 font-semibold">Citation</th>
                  <th className="py-2 pr-3 font-semibold">Owner</th>
                  <th className="py-2 pr-3 font-semibold">Due</th>
                  <th className="py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 pr-3 text-gray-900">
                      {r.title}
                      {r.category && (
                        <span className="ml-1.5 text-[11px] text-gray-400">{r.category}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500">{r.citation || '—'}</td>
                    <td className="py-2 pr-3 text-xs text-gray-600">{r.owner || 'Unassigned'}</td>
                    <td className="py-2 pr-3 text-xs tabular-nums text-gray-600">
                      {fmtDate(r.due_date)}
                    </td>
                    <td className="py-2">
                      <span
                        className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-medium ring-1 ${
                          PILL[r.status] || PILL.pending
                        }`}
                      >
                        {r.status_label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-500">
              {actionable > 0
                ? `${actionable} requirement${actionable === 1 ? '' : 's'} need attention, listed first.`
                : 'Nothing overdue or unevidenced.'}
            </p>
            {data.truncated && !showAll && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                View all {data.total}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
